import { Row, Col, Modal, Form, Input, Tabs, Badge } from "antd";
import React, { useState, useEffect } from "react";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import languages from "../../config/language";
import { MAKE_URL } from "../../constants/conts";
import VehicleManufacturerSelect from "../../components/select/VehicleManufacturerSelect";
import SegmentSelect from "../../components/select/SegmentSelect";
import FileUpload from "../../components/FileUpload";
const { TabPane } = Tabs;

const From = ({ path, sectionName, show, hide, data, refresh }) => {

  const [form]                  =   Form.useForm();
  const { request }             =   useRequest();
  const [loading, setLoading]   =   useState(false);
  const [images, setImages]     =   useState([]);
  const [validationStatus, setValidationStatus] = useState({ name: {}, display_name: {} });

  useEffect(() => {
    const initialNameStatus = {};
    const initialDisplayStatus = {};

    languages.forEach(lang => {
      initialNameStatus[lang.short] = data ? !!data[`${lang.short}_name`] : false;
      initialDisplayStatus[lang.short] = data ? !!data[`${lang.short}_display_name`] : false;
    });

    setValidationStatus({
      name: initialNameStatus,
      display_name: initialDisplayStatus,
    });

    if (data) {
      const mappedImages = data.image_urls && data.image_urls.length > 0
        ? data.image_urls.map((url, idx) => ({ uid: `existing-${idx}`, url }))
        : [];

      form.setFieldsValue({ ...data, images: mappedImages });
      setImages(mappedImages);
    } else {
      form.resetFields();
      setImages([]);
    }

  }, [data, form])

  const handleFieldChange = (value, lang, type) => {
    const fieldValue = form.getFieldValue(`${lang}_${type}`);

    setValidationStatus(prevStatus => ({
      ...prevStatus,
      [type]: {
        ...prevStatus[type],
        [lang]: !!fieldValue,
      }
    }));
  };

  const onCreate = async (values) => {

    const formData = new FormData();

    languages.forEach(lang => {
      formData.append(`${lang.short}_name`, values[`${lang.short}_name`] || "");
      formData.append(`${lang.short}_display_name`, values[`${lang.short}_display_name`] || "");
    });

    formData.append("segment_id", values.segment_id || "");
    formData.append("manufacturer_id", values.manufacturer_id || "");

    // Handle multiple images
    const imageList = values.images || [];
    if (Array.isArray(imageList)) {
      for (const file of imageList) {
        if (file.originFileObj) {
          formData.append("images", file.originFileObj);
        } else if (file.url) {
          // Existing image - extract the path from the URL
          const url = new URL(file.url);
          formData.append("existing_images", url.pathname);
        }
      }
    }

    setLoading(true);
    request({
      url: MAKE_URL(path, data),
      method: 'POST',
      data: formData,
      onSuccess: (data) => {
        setLoading(false);
        if (data.status) {
          ShowToast(data.message, Severty.SUCCESS);
          hide();
          refresh();
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (error) => {
        ShowToast(error.response.data.message, Severty.ERROR);
        setLoading(false);
      },
    })
  };

  const handleCancel = () => {
    setValidationStatus({ name: {}, display_name: {} });
    hide();
  };

  const handleImages = (data) => {
    setImages(data);
    form.setFieldValue('images', data);
  };

  return (
    <Modal
      open = {show}
      title = {`${data ? 'Update ' + sectionName : 'Create a New ' + sectionName}`}
      okText = "Ok"
      onCancel = {handleCancel}
      width={800}
      okButtonProps = {{
        form: 'create',
        htmlType: 'submit',
        loading: loading,
      }}
    >
      <Form id="create" form={form} onFinish={onCreate} layout="vertical">
        
        <Row gutter={[16, 0]}>
          
          <Col span={24} xs={12} sm={12} md={12}>
            <Form.Item
              label="Select Segments"
              name="segment_id"
              required
              rules={[{ required: true, message: "Please select segment!" }]}
            >
              <SegmentSelect
                value={form.getFieldValue("segment_id") || []}
                onChange={(val) => form.setFieldsValue({ segment_id: val })}
              />
            </Form.Item>
          </Col>

          <Col span={24} xs={12} sm={12} md={12}>
            <Form.Item
              name="manufacturer_id"
              label="Select Manufacturer"
              rules={[{ required: true, message: "Please select manufacturer" }]}
            >
              <VehicleManufacturerSelect
                value={form.getFieldValue("manufacturer_id")}
                onChange={(val) => form.setFieldValue("manufacturer_id", val)}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={24} md={24}>
            <Form.Item
              label="Upload Images (Multiple)"
              name="images"
            >
              <FileUpload
                filterType={["image"]}
                btnName={'Image'}
                multiple={true}
                maxCount={10}
                onChange={(data) => handleImages(data)}
              />
            </Form.Item>
          </Col>

          <Col span={24} xs={12} sm={12} md={24}>
            <Tabs defaultActiveKey={languages[0].short}>
              {languages.map(lang => (
                <TabPane 
                  tab={
                    <span>
                      {lang.long}
                      {(
                        validationStatus.name[lang.short] === false ||
                        validationStatus.display_name[lang.short] === false
                      ) && <Badge dot color="red" className="ml-1" />}
                    </span>
                  }
                  key={lang.short}
                >
                  <Form.Item
                    label={`Name (${lang.long})`}
                    name={`${lang.short}_name`}
                    rules={[
                      ...(lang.short === 'en' ? [
                        { required: true, message: `Please enter the name in ${lang.long}!` }
                      ] : []),
                      { max: 100, message: "Name should not contain more than 100 characters!" },
                      { min: 2, message: "Name should contain at least 2 characters!" },
                    ]}
                    onChange={(e) => handleFieldChange(e.target.value, lang.short, 'name')}
                  >
                    <Input autoComplete="off" placeholder={`Enter name in ${lang.long}`} />
                  </Form.Item>

                  <Form.Item
                    label={`Display Name (${lang.long})`}
                    name={`${lang.short}_display_name`}
                    rules={[
                      ...(lang.short === 'en' ? [
                        { required: true, message: `Please enter the display name in ${lang.long}!` }
                      ] : []),
                      { max: 100, message: "Display name should not contain more than 100 characters!" },
                      { min: 2, message: "Display name should contain at least 2 characters!" },
                    ]}
                    onChange={(e) => handleFieldChange(e.target.value, lang.short, 'display_name')}
                  >
                    <Input autoComplete="off" placeholder={`Enter display name in ${lang.long}`} />
                  </Form.Item>
                </TabPane>
              ))}
            </Tabs>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
};

export default From;